import { evaluateCondition } from "./logic";
import { entryValue } from "./types";
import type { Question, RepeatField } from "./types";
import { byPos } from "./sortKey";

export const MAX_REPEAT_ENTRIES = 100;
export const MAX_REPEAT_FIELDS = 20;
export function repeatFieldsOf(question: Question): RepeatField[] {
  return [...(question.config?.fields ?? [])].sort(byPos);
}
export function entryLabelOf(question: Question): string {
  return question.config?.entryLabel?.trim() || "Entry";
}
export function maxEntriesOf(question: Question): number {
  const max = question.config?.maxEntries;
  return Number.isInteger(max) && max! > 0
    ? Math.min(max!, MAX_REPEAT_ENTRIES)
    : MAX_REPEAT_ENTRIES;
}
export function minEntriesOf(question: Question): number {
  const min = question.config?.minEntries;
  return Number.isInteger(min) && min! >= 0 ? min! : 0;
}
/** Reuse the existing scalar renderer and validator for fields within entries. */
export function fieldQuestion(field: RepeatField): Question {
  return { ...field, sectionId: "" };
}

/** Child visibility is evaluated within an entry, against earlier visible fields only. */
export function visibleEntryFields(
  question: Question,
  entry: import("./types").RepeatedEntry,
): RepeatField[] {
  const fields = repeatFieldsOf(question);
  const byId = new Map(fields.map((field) => [field.id, fieldQuestion(field)]));
  const effective: import("./types").AnswerMap = {};
  return fields.filter((field) => {
    if (
      field.visibleWhen &&
      !evaluateCondition(field.visibleWhen, effective, byId)
    )
      return false;
    const value = entryValue(entry, field.id);
    if (value) effective[field.id] = value;
    return true;
  });
}

/** Hidden and display-only fields do not count as answers or survive submission. */
export function visibleRepeatedAnswer(
  question: Question,
  value: import("./types").AnswerValue | undefined,
): import("./types").AnswerValue | undefined {
  if (value?.kind !== "entries") return value;
  return {
    ...value,
    entries: value.entries.map((entry) => ({
      ...entry,
      values: Object.fromEntries(
        visibleEntryFields(question, entry)
          .filter((field) => field.type !== "text_block")
          .flatMap((field) => {
            const value = entry.values[field.id];
            return value === undefined ? [] : [[field.id, value]];
          }),
      ),
    })),
  };
}
